import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    FormControl,
    FormLabel,
    FormGroup,
    Alert,
    ButtonGroup,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import React, { useState, useEffect} from 'react';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dateFns, { format } from 'date-fns';
import { validateQRCode } from '../validate/validateQRCode';


const QRPassCreateDialog = ({ customer, editValues, open, onClose, onCreate }) => {

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [amount, setAmount] = useState('');
  const [checkIn, setCheckIn] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [isCheckInDisabled, setIsCheckInDisabled] = useState(false);

  const initialValues = {
    startDate: new Date().toISOString(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
    checkIn: true,
    period: '1M',
    amount: '0.0'
  };

  useEffect(() => {
    const valuesToSet = editValues ? editValues : initialValues;
    setStartDate(new Date(valuesToSet.startDate));
    setEndDate(new Date(valuesToSet.endDate));
    setAmount(valuesToSet.amount);
    setCheckIn(valuesToSet.checkIn);
    if (valuesToSet?.period) {
      setSelectedPeriod(valuesToSet.period);
    }

    // Update checkIn state
    updateCheckInState(valuesToSet.startDate);

  }, [editValues]);

  const handleCreate = async () => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const createValues = {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
        amount
      }

      const { error } = validateQRCode(createValues);
      if (error) {
        setError(error.details[0].message);
        return;
      }
      
      onCreate(createValues, !isCheckInDisabled && checkIn);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.msg) {
        setError(error.response.data.msg);
      } else {
        setError(error.message);
      }
      console.error('Error when creating QRCode:', error);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setSelectedPeriod(null);
    
    // Update checkIn state
    updateCheckInState(date);
  };
  
  const updateCheckInState = (date) => {
    const disabled = !date || (format(new Date(date), 'yyyy-MM-dd') > format(new Date(), 'yyyy-MM-dd'));
    setIsCheckInDisabled(disabled);
  }

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setSelectedPeriod(null);
  };

  const handleCheckInChange = (checkIn) => {
    setCheckIn(checkIn);
  };

  const setDates = (period) => {
    setSelectedPeriod(period);
    switch (period) {
        case '1D':
            setStartDate(new Date());
            setEndDate(new Date());
            break;
        case '1M':
            setStartDate(new Date());
            setEndDate(new Date(new Date().setMonth(new Date().getMonth() + 1)));
            break;
        case '3M':
            setStartDate(new Date());
            setEndDate(new Date(new Date().setMonth(new Date().getMonth() + 3)));
            break;
        case '6M':
            setStartDate(new Date());
            setEndDate(new Date(new Date().setMonth(new Date().getMonth() + 6)));
            break;
        case '1Y':
            setStartDate(new Date());
            setEndDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
            break;
        default:
            break;
    }
    // Update checkIn state
    updateCheckInState(new Date());
  };

  return (
    <Dialog open={open} onClose={onClose}>
      
      <DialogTitle>QR Pass Create</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControl component="fieldset">
            <FormLabel component="legend">
                Customer
            </FormLabel>
            <FormGroup>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                    padding: '0px',
                    marginBottom: '32px'
                }}>
                <Typography variant="body1">Name: {customer.name}</Typography>
                <Typography variant="body1">Phone: {customer.phone}</Typography>
                <Typography variant="body1">Email: {customer.email}</Typography>
                </Box>
            </FormGroup>
        </FormControl>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '20px',
        }}>
          <Typography variant="subtitle2">Select Period:</Typography>
          <ButtonGroup size="small" aria-label="period-buttons">
            {['1D', '1M', '3M', '6M', '1Y'].map((period) => (
              <Button
                key={period}
                variant={selectedPeriod === period ? 'contained' : 'outlined'}
                onClick={() => setDates(period)}
                sx={{ height: 36 }}
              >
                {period}
              </Button>
            ))}
          </ButtonGroup>
          <FormControlLabel
            control={
                <Checkbox
                    checked={!isCheckInDisabled && checkIn}
                    onChange={(e) => handleCheckInChange(e.target.checked)}
                    name="checkIn"
                    disabled={isCheckInDisabled}
                />
            }
            label="Check In"
          />
        </Box>

        <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            padding: '0px'
        }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label="Start Date"
                    value={startDate}
                    format="dd.MM.yyyy"
                    onChange={handleStartDateChange}
                    fullWidth
                    margin="normal"
                    slotProps={{
                      popper: {
                        placement: 'left-start', // for laptops and smaller screens
                      }
                    }}
                />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label="Expire Date"
                    value={endDate}
                    format="dd.MM.yyyy"
                    onChange={handleEndDateChange}
                    fullWidth
                    margin="normal"
                    slotProps={{
                      popper: {
                        placement: 'right-start', // for laptops and smaller screens
                      }
                    }}
                />
            </LocalizationProvider>
        </Box>
        <TextField
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            type="number"
            fullWidth
            margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleCreate}>Create</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRPassCreateDialog;
